// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { QRCodeCanvas } from 'qrcode.react'
import { FC, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
// component import
import { Alert } from '../common/Alert'
import { CustomController } from '../common/CustomController'
import { CustomModal } from '../common/CustomModal'
// others
import { useConfirmMfaAuthEnrollmentMutation, useStartMfaAuthEnrollmentMutation } from '../../../generated'
import {
  AUTHENTICATOR_ENABLED_SUCCESS,
  AUTHENTICATOR_MANUAL_ENTRY,
  AUTHENTICATOR_SCAN_INSTRUCTION,
  CONFIRM,
  MFA_ERROR_CODES,
  SET_UP_AUTHENTICATOR,
  SOMETHING_WENT_WRONG
} from '../../constants'
import { MfaAuthEnrollmentModalProps, MfaOtpFormValues } from '../../interfaceTypes'
import { mfaOtpValidationSchema } from '../../validationSchema'

/**
 * Authenticator setup. The secret is shown once, and the method is only switched on
 * after the user proves their app reproduces a code from it — so a mis-scan cannot
 * leave them with a factor they are unable to satisfy at the next login.
 */
export const MfaAuthEnrollmentModal: FC<MfaAuthEnrollmentModalProps> = ({ isOpen, handleClose, onEnrolled }) => {
  const methods = useForm<MfaOtpFormValues>({
    mode: 'all',
    resolver: yupResolver(mfaOtpValidationSchema),
    defaultValues: { otp: '' }
  })

  const { handleSubmit, reset } = methods

  const reportError = (message: string) => {
    // The global Apollo link already surfaces the API's own wording for known codes.
    if (!MFA_ERROR_CODES.includes(message)) Alert.error(SOMETHING_WENT_WRONG)
  }

  const [startEnrollment, { data: enrollment, loading: starting }] = useStartMfaAuthEnrollmentMutation({
    fetchPolicy: 'no-cache',
    onError({ message }) {
      reportError(message)
      handleClose()
    }
  })

  const [confirmEnrollment, { loading: confirming }] = useConfirmMfaAuthEnrollmentMutation({
    onError({ message }) {
      reportError(message)
      reset({ otp: '' })
    },

    onCompleted(data) {
      if (!data) return

      const { confirmMfaAuthEnrollment } = data
      const { response, user } = confirmMfaAuthEnrollment
      const { status } = response || {}

      if (status === 200 && user) {
        Alert.success(AUTHENTICATOR_ENABLED_SUCCESS)
        onEnrolled(!!user.mfaEmailEnabled, !!user.mfaAuthEnabled)
        handleClose()
      }
    }
  })

  // A fresh secret per opening: reopening after abandoning setup must not resurrect the
  // previous one, which the API has already replaced.
  useEffect(() => {
    if (isOpen) {
      reset({ otp: '' })
      startEnrollment()
    }
    // eslint-disable-next-line
  }, [isOpen])

  const { secret, otpauthUrl } = enrollment?.startMfaAuthEnrollment || {}

  const onSubmit = async ({ otp }: MfaOtpFormValues) => {
    await confirmEnrollment({ variables: { confirmMfaAuthEnrollmentInput: { code: otp } } })
  }

  return (
    <CustomModal isOpen={isOpen} handleClose={handleClose} title={SET_UP_AUTHENTICATOR} maxWidth="xs" width="420px">
      {starting || !otpauthUrl ? (
        <Box display="flex" justifyContent="center" padding={4}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Typography variant="subtitle1" component="p" marginBottom={2}>
              {AUTHENTICATOR_SCAN_INSTRUCTION}
            </Typography>

            <Box display="flex" justifyContent="center" marginBottom={2}>
              <QRCodeCanvas value={otpauthUrl} size={180} />
            </Box>

            <Typography variant="subtitle1" component="p">
              {AUTHENTICATOR_MANUAL_ENTRY}
            </Typography>

            <Typography
              variant="body2"
              component="p"
              marginBottom={2}
              sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
            >
              {secret}
            </Typography>

            <CustomController controllerName="otp" controllerLabel="Verification code" fieldType="text" autoFocus />

            <Box marginTop={3}>
              <Button
                variant="contained"
                type="submit"
                fullWidth
                color="primary"
                disabled={confirming}
                aria-label={CONFIRM}
                endIcon={confirming && <CircularProgress size={20} color="inherit" />}
              >
                {CONFIRM}
              </Button>
            </Box>
          </form>
        </FormProvider>
      )}
    </CustomModal>
  )
}
